import { Navigate, useNavigate } from 'react-router-dom';

import { HeroBanner } from '../components/HeroBanner';
import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { Mascot } from '../components/Mascot';
import type { ProductSummary, RecommendItinerary, RecommendProduct } from '../lib/api';
import { formatPrice, pickI18n } from '../lib/format';
import { useKiosk } from '../lib/session';

/** 推薦卡左上角的金色書籤，裁自業主 2026-08-19 交付素材（4_05.png）。 */
const BADGE = '/brand/badge-recommend.png';

/** RecommendProduct → ProductSummary（推薦結果只帶子集欄位，補齊以接 Schedule 畫面）。 */
function toProductSummary(rp: RecommendProduct): ProductSummary {
  return {
    id: rp.product_id,
    sku: rp.sku,
    category: rp.category,
    name_i18n: rp.name_i18n,
    description_i18n: null,
    base_price: rp.base_price,
    currency: 'TWD',
    duration_minutes: rp.duration_minutes,
    channels: ['kiosk'],
    is_published: true,
  };
}

/**
 * 專屬行程 — 設計稿 2026-08-19 第 7 頁第 2 步。
 *
 * 每張卡片是一個行程組合：名稱、時長與單人價、組成內容、立即訂購。
 *
 * ## 為什麼獨立成一頁
 *
 * 原本結果是接在問卷下方，答完要往下捲才看得到。1920 高的直立機台上，捲動這件
 * 事本身就是操作門檻（設計稿也把它畫成獨立一頁）。
 *
 * ## 直接進來會被導回問卷
 *
 * 結果放在 draft 裡而不是網址上——推薦條件有四項，塞進 query string 只會做出一條
 * 沒人會用、又得另外驗證的入口。沒有結果就回問卷重問一次。
 */
export function RecommendResultScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, locale, draft, patchDraft } = useKiosk();

  const items = draft.recommendations;
  if (items === null || items === undefined) return <Navigate to="/recommend" replace />;

  /**
   * 「立即訂購」——整組行程一起買。
   *
   * 此前只帶走 `products[0]`，其餘無聲消失：顧客看到的是三小時、兩個項目的行程，
   * 按下訂購後買到的是一小時、一個項目，而畫面上沒有任何異狀。
   *
   * 單一非套票商品仍走既有的 `/schedule`——那一頁有促銷劃線、依日分組與推薦日期
   * 收斂，是設計稿畫的樣子。多項目或含套票才需要逐段選時段。
   */
  function pick(itin: RecommendItinerary): void {
    const products = itin.products.map(toProductSummary);
    if (products.length === 0) return;
    const single = products.length === 1 ? products[0] : undefined;
    if (single !== undefined && single.category !== 'package') {
      patchDraft({
        product: single,
        schedule: null,
        planProducts: null,
        plan: null,
        planTotal: null,
      });
      navigate('/schedule');
      return;
    }
    patchDraft({
      product: products[0] ?? null,
      schedule: null,
      planProducts: products,
      plan: null,
      planTotal: null,
    });
    navigate('/plan');
  }

  /** 「安平港灣巡禮(60分鐘) x 水上設施(60分鐘)」——設計稿的組成說明行。 */
  const composition = (itin: RecommendItinerary): string =>
    itin.products
      .map(
        (p) =>
          `${pickI18n(p.name_i18n, p.sku, locale)}(${p.duration_minutes} ${t('browse.minutes')})`,
      )
      .join(' x ');

  const partySize = Object.values(draft.quantities).reduce((a, b) => a + b, 0) || 1;

  return (
    <KioskFrame bleed>
      <HeroBanner height={340} align="left">
        <Mascot size={210} pose="cheer" />
      </HeroBanner>

      <h1 className="k-h1" style={{ textAlign: 'center', marginTop: 8, marginBottom: 6 }}>
        {t('recommend.resultTitle')}
      </h1>
      <p className="k-subtitle k-subtitle--flanked">{t('recommend.resultLead')}</p>

      {items.length === 0 && (
        <p className="k-text k-text--dim" style={{ textAlign: 'center' }}>
          {t('recommend.empty')}
        </p>
      )}

      {items.map((itin, index) => (
        <article key={itin.id} className="k-itinerary">
          {/* 金色書籤：設計稿每張卡都有，純裝飾故對輔助技術隱藏 */}
          <img className="k-itinerary__badge" src={BADGE} alt="" aria-hidden="true" />
          <h2 className="k-itinerary__name">
            {itin.products.map((p) => pickI18n(p.name_i18n, p.sku, locale)).join(' + ')}
          </h2>
          <p className="k-itinerary__meta">
            {itin.estimated_minutes} {t('browse.minutes')}
            <span className="k-itinerary__sep" aria-hidden="true" />
            {/* 單人價：總價已含全團，除回人數才對得上設計稿的「NT$999／人」 */}
            {formatPrice(String(Math.round(Number(itin.total_price) / partySize)))}
            {t('recommend.perPerson')}
          </p>
          <hr className="k-itinerary__rule" />
          <p className="k-itinerary__detail">{composition(itin)}</p>
          {itin.fit_note !== '' && <p className="k-itinerary__detail">{itin.fit_note}</p>}
          {/* 設計稿只有第一張是深藍實心，其餘白底金框——名次以此表達 */}
          <KioskButton variant={index === 0 ? 'primary' : 'secondary'} onClick={() => pick(itin)}>
            {t('recommend.orderNow')}
          </KioskButton>
        </article>
      ))}

      <div className="k-footbar" style={{ justifyContent: 'center', gap: 28 }}>
        <KioskButton variant="secondary" onClick={() => navigate('/home')}>
          ← {t('common.back')}
        </KioskButton>
        <KioskButton variant="secondary" onClick={() => navigate('/recommend')}>
          {t('recommend.reselect')} ↻
        </KioskButton>
      </div>
    </KioskFrame>
  );
}
